/**
 * «Фото чата — в салон» — кнопка у молнии в «Чатах» и её окно (не путать с пересылкой одного
 * сообщения — стрелка у пузыря, `Forward.tsx`).
 *
 * Подбор платья и интерес клиента сдать своё — одно и то же действие: фото из этого чата уходят
 * в группу Telegram салона «Общее <город>» с вопросом, сотрудники отвечают реплаем, ответ
 * привязывается к чату (полоса «Подбор» над полем ответа, вкладка «Подбор» сделки). В окне:
 * тип запроса (подбор / сдать наряд), фото клиента из чата — по умолчанию выбраны
 * все, город — по чату, его можно сменить, и пара слов салону.
 * Спека `docs/specs/2026-10-07-chat-podbor-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Images, X } from "lucide-react";
import { useEffect, useState } from "react";

import { type PodborKind, forwardToSalon, loadPodborForm } from "@/lib/salonForward";

const when = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}`;

function Window({ chatId, onClose }: { chatId: number; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["chat-podbor-form", chatId], queryFn: () => loadPodborForm(chatId), staleTime: 0 });
  const [kind, setKind] = useState<PodborKind | null>(null);
  const [city, setCity] = useState<number | null>(null);
  const [off, setOff] = useState<Set<number>>(new Set()); // снятые с отправки: по умолчанию идут все
  const [question, setQuestion] = useState("");
  const form = q.data;
  const nowKind = kind ?? form?.kind ?? "podbor";
  // Города для выбранного типа: «Все города» (общий чат приёма) — только у «Сдать наряд»; города
  // чата нет — сдать наряд по умолчанию туда.
  const cities = (form?.cities ?? []).filter((c) => !c.kinds || c.kinds.includes(nowKind));
  const can = (id: number | null) => (id !== null && cities.some((c) => c.id === id) ? id : null);
  const nowCity = can(city) ?? can(form?.city ?? null) ?? can(nowKind === "dress" ? 0 : null);
  const send = useMutation({
    mutationFn: (messages: number[]) => forwardToSalon(chatId, { kind: nowKind, city: nowCity!, messages, question }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["chat-podbor", chatId] });
      void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
      void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
      onClose();
    },
  });
  useEffect(() => {
    const escape = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onClose]);

  const photos = form?.photos ?? [];
  const picked = photos.filter((p) => !off.has(p.message_id)).map((p) => p.message_id);
  const all = picked.length === photos.length;
  const group = cities.find((c) => c.id === nowCity)?.group;
  const chip = (on: boolean) =>
    `rounded-full px-3 py-1 text-[14px] font-medium ${on ? "bg-[var(--tg-primary)] text-white" : "bg-[var(--tg-hover)] text-[var(--tg-text)]"}`;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="flex max-h-[92vh] w-full max-w-[640px] flex-col overflow-hidden rounded-t-[16px] bg-[var(--tg-surface)] text-[14px] text-[var(--tg-text)] shadow-[0_4px_24px_rgba(0,0,0,.28)] sm:rounded-[16px]"
        onClick={(e) => e.stopPropagation()}
        data-annot="окно: фото чата в салон"
      >
        <div className="flex items-center gap-2 px-4 pb-1 pt-3">
          <Images size={18} className="text-[var(--tg-primary)]" />
          <span className="flex-1 text-[16px] font-medium">Фото чата — в салон</span>
          <button type="button" onClick={onClose} title="Закрыть" className="grid size-8 place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]">
            <X size={18} />
          </button>
        </div>
        {q.isPending ? (
          <p className="px-4 py-6 text-[var(--tg-sec)]">Собираю фото чата…</p>
        ) : q.isError ? (
          <p className="px-4 py-6 text-[#e53935]">{q.error.message}</p>
        ) : (
          <div className="flex min-h-0 flex-col gap-3 overflow-y-auto px-4 pb-3 pt-1">
            <div className="flex flex-wrap gap-1.5" data-annot="тип запроса в салон">
              {form!.kinds.map((k) => (
                <button key={k.id} type="button" title={k.hint} onClick={() => setKind(k.id)} className={chip(nowKind === k.id)}>
                  {k.name}
                </button>
              ))}
            </div>
            <label className="flex flex-wrap items-center gap-2" data-annot="город запроса">
              <span className="text-[var(--tg-sec)]">Город</span>
              <select
                value={nowCity ?? ""}
                onChange={(e) => setCity(e.target.value === "" ? null : Number(e.target.value))}
                className="rounded-[10px] border border-[var(--tg-sec)]/30 bg-[var(--tg-surface)] px-2 py-1 outline-none focus:border-[var(--tg-primary)]"
              >
                {nowCity === null && <option value="">выберите город</option>}
                {cities.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {group && <span className="text-[13px] text-[var(--tg-sec)]">уйдёт в «{group}»</span>}
              {form!.city === null && nowCity === null && <span className="text-[13px] text-[#e53935]">город по чату не определён</span>}
            </label>
            <div data-annot="фото чата для салона">
              <div className="mb-1 flex items-center gap-2 text-[13px] text-[var(--tg-sec)]">
                <span>
                  Фото клиента из чата: выбрано {picked.length} из {photos.length}
                </span>
                {photos.length > 0 && (
                  <button type="button" className="text-[var(--tg-primary)]" onClick={() => setOff(all ? new Set(photos.map((p) => p.message_id)) : new Set())}>
                    {all ? "снять все" : "выделить все"}
                  </button>
                )}
              </div>
              {photos.length === 0 ? (
                <p className="text-[var(--tg-sec)]">Клиент не присылал фото в этот чат — пересылать нечего.</p>
              ) : (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(84px,1fr))] gap-1.5">
                  {photos.map((p) => {
                    const on = !off.has(p.message_id);
                    return (
                      <button
                        key={p.message_id}
                        type="button"
                        onClick={() =>
                          setOff((had) => {
                            const next = new Set(had);
                            if (on) next.add(p.message_id);
                            else next.delete(p.message_id);
                            return next;
                          })
                        }
                        title={`${when(p.date)}${p.sent ? " · уже пересылали в салон" : ""}`}
                        className={`relative aspect-[4/5] overflow-hidden rounded-[10px] bg-black/10 ${on ? "ring-2 ring-[var(--tg-primary)]" : "opacity-45"}`}
                      >
                        <img src={p.thumb} alt="" loading="lazy" className="size-full object-cover" />
                        {on && (
                          <span className="absolute right-1 top-1 grid size-5 place-items-center rounded-full bg-[var(--tg-primary)] text-white">
                            <Check size={13} />
                          </span>
                        )}
                        {p.sent && <span className="absolute inset-x-0 bottom-0 bg-black/60 px-1 text-[10.5px] text-white">уже в салоне</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <textarea
              rows={2}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={500}
              placeholder="Что спросить у салона (необязательно): размер, рост, дата, что клиент хочет"
              className="w-full resize-y rounded-[12px] border border-[var(--tg-sec)]/30 bg-transparent px-3 py-2 outline-none focus:border-[var(--tg-primary)]"
              data-annot="вопрос салону"
            />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--tg-sec)]/20 px-4 py-2.5">
          <button
            type="button"
            autoFocus
            disabled={!form || send.isPending || picked.length === 0 || nowCity === null}
            onClick={() => send.mutate(picked)}
            className="rounded-full bg-[var(--tg-primary)] px-4 py-1.5 font-medium text-white disabled:opacity-50"
            data-annot="отправить в салон"
          >
            {send.isPending ? "Отправляю…" : all ? `Отправить все фото (${picked.length})` : `Отправить выбранные (${picked.length})`}
          </button>
          <button type="button" onClick={onClose} className="rounded-full px-3 py-1.5 font-medium text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]">
            Отмена
          </button>
          {send.isError && (
            <span role="alert" className="text-[13px] text-[#e53935]">
              {send.error.message}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function SalonForwardButton({ chatId }: { chatId: number }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Фото чата — в салон: подбор платья или сдать наряд; фото уйдут в группу города"
        className="grid size-12 flex-none place-items-center rounded-full bg-[var(--tg-surface)] text-[var(--tg-sec)] shadow-[0_1px_8px_1px_rgba(0,0,0,.12)] hover:bg-[var(--tg-hover)]"
        data-annot="фото чата в салон"
      >
        <Images size={22} />
      </button>
      {open && <Window chatId={chatId} onClose={() => setOpen(false)} />}
    </>
  );
}
